import { formatCents } from '@/lib/format';

interface StatBandStats {
  activeEngagements: number;
  pastBookings: number;
  quoteCount: number;
  quoteMinCents: number | null;
  quoteMaxCents: number | null;
}

interface Props {
  stats: StatBandStats;
}

function StatTile({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1 rounded-2xl bg-card px-5 py-4 shadow-ambient">
      <span className="label-caps text-xs text-muted-foreground">{label}</span>
      <span className="display-tight text-2xl font-semibold">{value}</span>
    </div>
  );
}

function quoteRangeDisplay(min: number | null, max: number | null): string {
  if (min === null && max === null) return '—';
  if (min === max || max === null) return formatCents(min);
  if (min === null) return formatCents(max);
  return `${formatCents(min)} – ${formatCents(max)}`;
}

export default function VendorStatBand({ stats }: Props) {
  const hasActivity =
    stats.activeEngagements > 0 ||
    stats.pastBookings > 0 ||
    stats.quoteMinCents !== null ||
    stats.quoteMaxCents !== null;

  if (!hasActivity) {
    return (
      <div className="rounded-2xl bg-muted/40 px-6 py-4 text-sm text-muted-foreground">
        No activity yet — add this vendor to a project to start tracking engagements.
      </div>
    );
  }

  return (
    <div className="grid grid-cols-3 gap-4">
      <StatTile label="Active engagements" value={stats.activeEngagements} />
      <StatTile label="Past bookings" value={stats.pastBookings} />
      <StatTile
        label="Quote range"
        value={quoteRangeDisplay(stats.quoteMinCents, stats.quoteMaxCents)}
      />
    </div>
  );
}
